<template lang="pug">
.box
    v-server-table(
        :url='api',
        :name='tableName',
        ref='datatable',
        :columns='columns',
        :options='options'
    )
        template(slot='beforeTable')
            datatable-loader(:name='tableName')

        template(slot='beforeFilter')
            .m-l-sm.float-right.mode-filter.width-180
                multiselect(
                    v-model='selectedPayments',
                    :options='mapPaymentList',
                    :multiple='true',
                    :close-on-select='false',
                    :clear-on-select='false',
                    placeholder='All Payments',
                    label='name',
                    track-by='id',
                    selectLabel='',
                    deselectLabel='Remove',
                    @search-change='refreshPaymentList',
                    @close='setPaymentFilter',
                    :loading='paymentLoading'
                )
                    template(
                        slot='selection',
                        slot-scope='{ values, search, isOpen }'
                    )
                        span.multiselect__single(
                            v-if='values.length && !isOpen'
                        )
                            | {{ values.length }} payments selected

                        span.multiselect__single.hide(v-else)

    receipt(ref='receipt')
</template>

<script>
import { mapState } from 'vuex';

const TableName = 'VUETABLE_TRANSACTION';
import receiptMixin from '../../mixins/receipt';
import Feature from '@js/plugins/feature';
import paymentMixin from '@js/mixins/payments';
import tillFilterMixin from '../../components/till-filter/till-filter-mixin';

export default {
    mixins: [receiptMixin, paymentMixin, tillFilterMixin],

    data: function() {
        var that = this;
        let actionButtons = [];

        if (Feature.hasFeature('retail')) {
            actionButtons.push({
                title: 'Export Details',
                action: [
                    {
                        title: 'CSV',
                        callback: () => {
                            this.downloadExportDetails('CSV');
                        },
                    },
                    {
                        title: 'XLSX',
                        callback: () => {
                            this.downloadExportDetails('XLSX');
                        },
                    },
                    {
                        title: 'PDF',
                        callback: () => {
                            this.downloadExportDetails('PDF');
                        },
                    },
                ],
            });
        }

        return {
            transaction: null,
            tableName: TableName,
            columns: [
                'settled_date',
                'sequence_number',
                'receipt_type',
                'terminal_type',
                'card_type',
                'approval_code',
                'cashier',
                'outlet_name',
                'payment_name',
                'table_name',
                'pax',
                'total_amount',
            ],
            options: {
                texts: {
                    title: 'Transaction List',
                    description: 'Shows transaction details',
                },

                customFilters: ['outlet_id', 'since', 'until', 'filters'],
                useReportFilter: true,

                initFilters: {
                    outlet_id: that.$store.getters.activeOutlet,
                    since: that.$store.getters.startDate.format(),
                    until: that.$store.getters.endDate.format(),
                },
                sortable: ['settled_date', 'total_amount'],
                headings: {
                    settled_date: 'Receipt Date',
                    sequence_number: 'Receipt No.',
                    terminal_type: 'Terminal',
                    payment_name: 'Payment',
                    table_name: 'Table',
                    outlet_name: this.trans('general.outlet'),
                    pax: 'Pax',
                    total_amount: 'Grand Total (RM)',
                    amount: 'Tendered Amount (RM)',
                    balance: 'Change (RM)',
                    approval_code: 'Approval Code',
                    receipt_type: 'Receipt Type',
                    card_type: 'Card Type/Pay Type',
                },

                orderBy: {
                    column: 'settled_date',
                    ascending: 0,
                },

                templates: {
                    pax(h, row) {
                        return row.pax === null ? 'N/A' : row.pax;
                    },

                    table_name(h, row) {
                        return row.table_name === null
                            ? 'Takeaway'
                            : row.table_name;
                    },

                    sequence_number: (h, row) => {
                        return this.receiptNumberFormat(row);
                    },

                    settled_date: (h, row) => {
                        return this.receiptDateFormat(row.settled_date);
                    },

                    terminal_type: (h, row) => {
                        return row.terminal_type === null
                            ? ''
                            : row.terminal_type;
                    },

                    approval_code: (h, row) => {
                        return row.approval_code === null
                            ? ''
                            : row.approval_code;
                    },

                    card_type: (h, row) => {
                        return row.card_type === null ? '' : row.card_type;
                    },

                    receipt_type: (h, row) => {
                        return row.receipt_type.toUpperCase();
                    },

                    total_amount(h, row) {
                        return require('./type')(h, row);
                    },
                },
                rowClassCallback: row => 'pointer',
                rowClickCallback: row => {
                    this.showReceipt(row.id, row.refund_id, row.voided);
                },
                pdf_url: Laroute.route(
                    'franchise.report.transaction.exportAsFile',
                    { type: 'PDF', summary: false },
                ),
                csv_url: Laroute.route(
                    'franchise.report.transaction.exportAsFile',
                    { type: 'CSV', summary: true },
                ),
                xls_url: Laroute.route(
                    'franchise.report.transaction.exportAsFile',
                    { type: 'XLSX', summary: true },
                ),

                actionButtons: actionButtons,

                columnsClasses: {
                    total_amount: 'text-right',
                    balance: 'text-right',
                    amount: 'text-right',
                },
            },
            selectedPayments: [],
        };
    },

    computed: {
        ...mapState('tillFilter', ['filters']),

        api() {
            return Laroute.route('franchise.report.transaction.index');
        },

        retailData() {
            return Feature.hasFeature('retail');
        },

        mapPaymentList() {
            let array = this.paymentList;
            let opt = array.filter(e => e.id === 'OPT');

            if (opt.length === 0) {
                array.push({
                    id: 'OPT',
                    type: 1,
                    name: 'OPT',
                });
            }

            return array;
        },
    },

    watch: {
        filters: function(to, from) {
            if (to === from) return;

            this.setFilter();
        },
    },

    mounted() {
        this.retailEnabled();
        this.getPaymentListIfNecessary();
    },

    methods: {
        downloadExportDetails(type) {
            let url = Laroute.route(
                'franchise.report.transaction.exportAsFile',
                { type, summary: true },
            );

            window.open(
                `${url}?${this.$refs.datatable.getQueryParams() +
                    '&details=true'}`,
            );
        },

        showReceipt(transaction_id, refund_id = null, voided = false) {
            this.$refs.receipt.$emit(
                'show-receipt',
                transaction_id,
                refund_id,
                voided,
            );
        },

        retailEnabled() {
            if (this.retailData == true) {
                this.columns[9] = 'amount';
                this.columns[10] = 'balance';
            }
        },

        setPaymentFilter() {
            this.$store.commit(`${this.tableName}/SET_CUSTOM_FILTER`, {
                filter: 'payments_id',
                value: this.selectedPayments.map(t => t.id),
            });
        },

        setFilter() {
            this.$store.commit(`${this.tableName}/SET_CUSTOM_FILTER`, {
                filter: 'filters',
                value: this.filters,
            });
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/transaction/vuetable_transaction.vue